import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3, LineChart as LineChartIcon, TrendingUp, TrendingDown, Minus, Loader2, X } from 'lucide-react';
import { MaintHelpTip } from './MaintenanceHelp';

const PALETTE = ['#2563eb', '#f97316', '#10b981', '#8b5cf6', '#ef4444', '#eab308', '#06b6d4', '#ec4899'];
const OTHER_COLOR = '#94a3b8';
const MAX_SERIES = 8;

const moneyShort = (v) => {
  const n = Number(v || 0);
  return n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n)}`;
};
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (mk) => {
  const [y, m] = mk.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${String(y).slice(2)}`;
};

const TrendBadge = ({ pct, state }) => {
  if (state === 'new') return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400" title="First costed spend in the last 3 months — no prior 3-month baseline to compare yet"><TrendingUp className="w-3 h-3" />new</span>;
  if (state !== 'pct' || pct == null) return <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" title="No change vs the prior 3-month average"><Minus className="w-3 h-3" />steady</span>;
  if (pct > 15) return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600 dark:text-red-400"><TrendingUp className="w-3 h-3" />{pct.toFixed(0)}%</span>;
  if (pct > 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400"><TrendingUp className="w-3 h-3" />{pct.toFixed(0)}%</span>;
  if (pct < 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"><TrendingDown className="w-3 h-3" />{Math.abs(pct).toFixed(0)}%</span>;
  return <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"><Minus className="w-3 h-3" />steady</span>;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => Number(p.value) > 0).sort((a, b) => b.value - a.value);
  const total = payload.reduce((s, p) => s + Number(p.value || 0), 0);
  return (
    <div className="rounded-md border bg-popover text-popover-foreground shadow-md p-2 text-xs max-w-[220px]">
      <p className="font-semibold mb-1">{label} — {money(total)}</p>
      {rows.slice(0, 10).map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />{p.name}</span>
          <span className="font-medium">{money(p.value)}</span>
        </div>
      ))}
      {rows.length === 0 && <p className="text-muted-foreground">No spend</p>}
      <p className="text-[10px] text-muted-foreground mt-1">Click to filter the report below</p>
    </div>
  );
};

export const CostTrends = ({ api, onFilter }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(() => sessionStorage.getItem('svcCostTrendMode') || 'stack');
  const [groupBy, setGroupBy] = useState(() => sessionStorage.getItem('svcCostTrendGroup') || 'vehicle');
  const [monthsBack, setMonthsBack] = useState(() => Number(sessionStorage.getItem('svcCostTrendMonths')) || 12);
  const [chip, setChip] = useState(null); // { label, monthKey }

  useEffect(() => { sessionStorage.setItem('svcCostTrendMode', mode); }, [mode]);
  useEffect(() => { sessionStorage.setItem('svcCostTrendGroup', groupBy); }, [groupBy]);
  useEffect(() => { sessionStorage.setItem('svcCostTrendMonths', String(monthsBack)); }, [monthsBack]);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await api.get(`/service/cost-trend?months=${monthsBack}&group_by=${groupBy}`);
        if (!gone) setData(res.data);
      } catch { /* keep last data on refetch errors */ }
      finally { if (!gone) setLoading(false); }
    })();
    return () => { gone = true; };
  }, [api, monthsBack, groupBy]);

  const { top, chartData } = useMemo(() => {
    if (!data) return { top: [], chartData: [] };
    const series = data.series || [];
    const topS = series.slice(0, MAX_SERIES);
    const rest = series.slice(MAX_SERIES);
    const rows = (data.months || []).map((mk, i) => {
      const row = { month: monthLabel(mk), key: mk };
      topS.forEach((s) => { row[s.key || s.device_id] = s.monthly[i]; });
      if (rest.length) row.__other = Math.round(rest.reduce((sum, s) => sum + (s.monthly[i] || 0), 0) * 100) / 100;
      return row;
    });
    return { top: topS, chartData: rows };
  }, [data]);

  const pick = (s, monthKey) => {
    if (!s) return;
    setChip({ label: s.label, monthKey: monthKey || null });
    onFilter({ deviceId: s.device_id || null, task: s.task || null, monthKey: monthKey || null });
  };
  const clearChip = () => { setChip(null); onFilter({ deviceId: 'all', task: null, monthKey: null }); };

  const hasSpend = (data?.series || []).some((s) => s.total > 0);

  return (
    <Card data-testid="service-cost-trends">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4 text-orange-600" />
            Spend Trends
            <span className="text-xs font-normal text-muted-foreground">last {monthsBack} months</span>
            <MaintHelpTip topic="cost_trend" testId="service-help-cost-trend" />
          </p>
          <div className="ml-auto flex items-center gap-1">
            {chip && (
              <button type="button" onClick={clearChip} data-testid="service-cost-trend-chip"
                className="inline-flex items-center gap-1 rounded-full bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 px-2 py-0.5 text-[11px] font-medium mr-1">
                {chip.label}{chip.monthKey ? ` · ${monthLabel(chip.monthKey)}` : ''}
                <X className="w-3 h-3" />
              </button>
            )}
            <Button variant={groupBy === 'vehicle' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs"
              onClick={() => { setGroupBy('vehicle'); setChip(null); }} data-testid="service-cost-trend-group-vehicle">Vehicles</Button>
            <Button variant={groupBy === 'task' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs"
              onClick={() => { setGroupBy('task'); setChip(null); }} data-testid="service-cost-trend-group-task">Tasks</Button>
            <span className="w-px h-4 bg-border mx-1" />
            {[3, 6, 12].map((n) => (
              <Button key={n} variant={monthsBack === n ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs"
                onClick={() => setMonthsBack(n)} data-testid={`service-cost-trend-months-${n}`}>{n}m</Button>
            ))}
            <span className="w-px h-4 bg-border mx-1" />
            <Button variant={mode === 'stack' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" title="Stacked bars"
              onClick={() => setMode('stack')} data-testid="service-cost-trend-mode-stack"><BarChart3 className="w-3.5 h-3.5" /></Button>
            <Button variant={mode === 'lines' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" title="Lines per vehicle"
              onClick={() => setMode('lines')} data-testid="service-cost-trend-mode-lines"><LineChartIcon className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center h-[240px]"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : !hasSpend ? (
          <div className="flex items-center justify-center h-[120px] text-sm text-muted-foreground" data-testid="service-cost-trend-empty">
            No costed services in the last {monthsBack} months — log services with a cost to build the trend.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
            <div>
              <div className="h-[240px]" data-testid="service-cost-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  {mode === 'stack' ? (
                    <BarChart data={chartData} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'currentColor' }} className="text-muted-foreground" tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} className="text-muted-foreground" tickFormatter={moneyShort} tickLine={false} axisLine={false} width={52} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fillOpacity: 0.08 }} />
                      {top.map((s, i) => (
                        <Bar key={s.key || s.device_id} dataKey={s.key || s.device_id} name={s.label} stackId="spend" fill={PALETTE[i % PALETTE.length]}
                          className="cursor-pointer" onClick={(d) => pick(s, d && d.payload ? d.payload.key : null)} />
                      ))}
                      {chartData.some((r) => r.__other > 0) && <Bar dataKey="__other" name={groupBy === 'task' ? 'Other tasks' : 'Other vehicles'} stackId="spend" fill={OTHER_COLOR} fillOpacity={0.6} />}
                    </BarChart>
                  ) : (
                    <LineChart data={chartData} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'currentColor' }} className="text-muted-foreground" tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} className="text-muted-foreground" tickFormatter={moneyShort} tickLine={false} axisLine={false} width={52} />
                      <Tooltip content={<ChartTooltip />} />
                      {top.map((s, i) => (
                        <Line key={s.key || s.device_id} type="monotone" dataKey={s.key || s.device_id} name={s.label} stroke={PALETTE[i % PALETTE.length]}
                          strokeWidth={2} dot={{ r: 2.5 }}
                          activeDot={{ r: 5, className: 'cursor-pointer', onClick: (e, p) => pick(s, p && p.payload ? p.payload.key : null) }} />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {top.map((s, i) => (
                  <button key={s.key || s.device_id} type="button" onClick={() => pick(s, null)} data-testid={`service-cost-trend-legend-${s.key || s.device_id}`}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] hover:bg-muted transition-colors">
                    <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="max-w-[140px] truncate">{s.label}</span>
                  </button>
                ))}
                {(data?.series || []).length > MAX_SERIES && (
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">
                    <span className="w-2 h-2 rounded-full" style={{ background: OTHER_COLOR }} />+{data.series.length - MAX_SERIES} more
                  </span>
                )}
              </div>
            </div>
            <div className="lg:border-l lg:pl-4">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{groupBy === 'task' ? 'Top tasks' : 'Top spenders'} · ▲ vs prior 3-mo avg</p>
              <div className="space-y-1" data-testid="service-cost-trend-top-spenders">
                {(data?.series || []).slice(0, 5).map((s, i) => (
                  <button key={s.key || s.device_id} type="button" onClick={() => pick(s, null)} data-testid={`service-cost-trend-top-${s.key || s.device_id}`}
                    className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted transition-colors">
                    <span className="text-[10px] font-semibold text-muted-foreground w-3">{i + 1}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-medium truncate">{s.label}</span>
                      <TrendBadge pct={s.trend_pct} state={s.trend_state} />
                    </span>
                    <span className="text-xs font-semibold whitespace-nowrap">{money(s.total)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
